import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { tournamentService } from '../../../services/tournamentService';

// Adapted from league-organizer/components/KnockoutBracket.jsx.
// Teams come directly from the tournament's active roster (tournament_teams)
// instead of league standings, and games are saved with a stage_name /
// match_notes pair like "Knockout - Semi Final".
const TournamentKnockoutBracket = ({ tournamentId, teams = [], onClose, onSuccess }) => {
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [bracketSettings, setBracketSettings] = useState({
    startDate: '',
    venue: '',
    timeSlot: '14:00',
  });
  const [stageConfigs, setStageConfigs] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const toggleTeamSelection = (teamId) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const generateBracket = () => {
    if (selectedTeams.length < 2) {
      setError('Please select at least 2 teams for the knockout bracket');
      return;
    }
    if (!bracketSettings.startDate) {
      setError('Please set a start date for the bracket');
      return;
    }

    setError(null);

    const numTeams = selectedTeams.length;
    const nextPowerOf2 = Math.pow(2, Math.ceil(Math.log2(numTeams)));
    const numByes = nextPowerOf2 - numTeams;

    const selected = teams.filter((t) => selectedTeams.includes(t.id));
    const shuffledTeams = [...selected].sort(() => Math.random() - 0.5);

    let currentRound = shuffledTeams.map((team, index) => ({
      id: `match-${index}`,
      team1: team,
      team2: null,
      winner: null,
      matchNumber: index + 1,
    }));

    if (numByes > 0) {
      const teamsWithByes = currentRound.slice(0, numByes);
      teamsWithByes.forEach((match) => {
        match.team2 = null;
        match.winner = match.team1;
      });
    }

    const firstRoundMatches = [];
    const teamsToPair = currentRound.slice(numByes);

    for (let i = 0; i < teamsToPair.length; i += 2) {
      if (i + 1 < teamsToPair.length) {
        firstRoundMatches.push({
          id: `match-${firstRoundMatches.length + 1}`,
          team1: teamsToPair[i].team1,
          team2: teamsToPair[i + 1].team1,
          winner: null,
          matchNumber: firstRoundMatches.length + 1,
          round: 1,
        });
      }
    }

    const byeMatches = currentRound.slice(0, numByes).map((match, index) => ({
      id: `match-bye-${index + 1}`,
      team1: match.team1,
      team2: null,
      winner: match.team1,
      matchNumber: firstRoundMatches.length + index + 1,
      round: 1,
      isBye: true,
    }));

    const rounds = [
      {
        roundNumber: 1,
        name: `Round of ${nextPowerOf2}`,
        matches: [...firstRoundMatches, ...byeMatches],
      },
    ];

    let roundNumber = 2;
    let previousRoundMatches = rounds[0].matches;

    while (previousRoundMatches.length > 1) {
      const nextRoundMatches = [];
      const roundName =
        previousRoundMatches.length === 2
          ? 'Final'
          : previousRoundMatches.length === 4
          ? 'Semi-Finals'
          : `Round ${roundNumber}`;

      for (let i = 0; i < previousRoundMatches.length; i += 2) {
        nextRoundMatches.push({
          id: `match-r${roundNumber}-${nextRoundMatches.length + 1}`,
          team1: null,
          team2: null,
          winner: null,
          matchNumber: nextRoundMatches.length + 1,
          round: roundNumber,
          previousMatch1: previousRoundMatches[i]?.id,
          previousMatch2: previousRoundMatches[i + 1]?.id,
        });
      }

      rounds.push({ roundNumber, name: roundName, matches: nextRoundMatches });
      previousRoundMatches = nextRoundMatches;
      roundNumber++;
    }

    const defaultStageConfigs = rounds.map((round) => {
      let defaultName = round.name;
      if (round.name === 'Final') defaultName = 'Finals';
      else if (round.name === 'Semi-Finals') defaultName = 'Semi Finals';
      else if (round.matches.length === 4) defaultName = 'Quarter Finals';

      return {
        roundNumber: round.roundNumber,
        stageName: defaultName,
        includeThirdPlace: false,
        thirdPlaceStageName: 'Third Place',
      };
    });

    setStageConfigs(defaultStageConfigs);
    setBracket({ rounds });
  };

  const updateStageConfig = (roundNumber, field, value) => {
    setStageConfigs((prev) =>
      prev.map((config) => (config.roundNumber === roundNumber ? { ...config, [field]: value } : config))
    );
  };

  const saveBracket = async () => {
    if (!bracket || !tournamentId) return;

    setIsGenerating(true);
    setError(null);

    try {
      const games = [];
      const baseDate = new Date(bracketSettings.startDate);
      let daysOffset = 0;

      bracket.rounds.forEach((round, roundIndex) => {
        const stageConfig = stageConfigs.find((c) => c.roundNumber === round.roundNumber);
        const stageName = stageConfig?.stageName || round.name;

        round.matches.forEach((match) => {
          if (match.isBye) return;

          const matchDate = new Date(baseDate);
          matchDate.setDate(matchDate.getDate() + daysOffset);
          const [hours, minutes] = bracketSettings.timeSlot.split(':');
          matchDate.setHours(parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0, 0, 0);

          games.push({
            tournamentId,
            homeTeamId: match.team1?.id || null,
            awayTeamId: match.team2?.id || null,
            scheduledDate: matchDate.toISOString(),
            venue: bracketSettings.venue || 'TBD',
            gameStatus: 'scheduled',
            stageName,
            matchNotes: `Knockout - ${stageName} (Match ${match.matchNumber})`,
          });

          daysOffset += 1;
        });

        if (roundIndex < bracket.rounds.length - 1) {
          daysOffset += 1;
        }

        if (stageConfig?.includeThirdPlace && round.matches.length === 2) {
          const thirdPlaceStageName = stageConfig.thirdPlaceStageName || 'Third Place';
          const matchDate = new Date(baseDate);
          matchDate.setDate(matchDate.getDate() + daysOffset);
          const [hours, minutes] = bracketSettings.timeSlot.split(':');
          matchDate.setHours(parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0, 0, 0);

          games.push({
            tournamentId,
            homeTeamId: null,
            awayTeamId: null,
            scheduledDate: matchDate.toISOString(),
            venue: bracketSettings.venue || 'TBD',
            gameStatus: 'scheduled',
            stageName: thirdPlaceStageName,
            matchNotes: `Knockout - ${thirdPlaceStageName}`,
          });

          daysOffset += 1;
        }
      });

      // Games with a TBD side (byes further up the bracket) cannot be
      // created because home/away team ids are required by the schema.
      const creatable = games.filter((g) => g.homeTeamId && g.awayTeamId);
      const skipped = games.length - creatable.length;

      if (creatable.length === 0) {
        throw new Error('No fully-determined matches to create yet. Add more teams or fill in later rounds manually.');
      }

      await tournamentService.createGamesBatch(creatable);

      setSuccess(
        `Successfully created ${creatable.length} knockout match(es)!${
          skipped > 0 ? ` ${skipped} later-round match(es) were skipped because their teams are not yet determined.` : ''
        }`
      );

      if (onSuccess) onSuccess();

      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      console.error('Error saving knockout bracket:', err);
      setError(err.message || 'Failed to save knockout bracket');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Create Knockout Bracket</h2>
          <p className="text-sm text-muted-foreground">Select active tournament teams to build a single-elimination bracket</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={18} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle2" size={18} />
            <p className="font-medium">{success}</p>
          </div>
        </div>
      )}

      {!bracket ? (
        <>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                <input
                  type="date"
                  value={bracketSettings.startDate}
                  onChange={(e) => setBracketSettings((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Time Slot</label>
                <input
                  type="time"
                  value={bracketSettings.timeSlot}
                  onChange={(e) => setBracketSettings((prev) => ({ ...prev, timeSlot: e.target.value }))}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Venue</label>
              <input
                type="text"
                value={bracketSettings.venue}
                onChange={(e) => setBracketSettings((prev) => ({ ...prev, venue: e.target.value }))}
                placeholder="Enter venue name"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">Select Teams</h3>
              <p className="text-sm text-muted-foreground">{selectedTeams.length} team(s) selected</p>
            </div>

            {teams.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No active teams in this tournament yet</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-2 border border-border rounded-lg p-4">
                {teams.map((team) => {
                  const isSelected = selectedTeams.includes(team.id);
                  return (
                    <div
                      key={team.id}
                      onClick={() => toggleTeamSelection(team.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-primary bg-primary' : 'border-border'
                        }`}
                      >
                        {isSelected && <Icon name="Check" size={14} className="text-primary-foreground" />}
                      </div>
                      {team.logoUrl ? (
                        <Image src={team.logoUrl} alt={team.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Icon name="Shield" size={20} className="text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{team.name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={generateBracket}
              disabled={selectedTeams.length < 2 || !bracketSettings.startDate}
              iconName="Zap"
              iconPosition="left"
            >
              Generate Bracket
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-4 bg-muted/30 rounded-lg p-4 border border-border">
            <div>
              <h3 className="text-lg font-medium text-foreground">Stage Configuration</h3>
              <p className="text-sm text-muted-foreground">Rename stages before saving</p>
            </div>

            <div className="space-y-4">
              {stageConfigs.map((config) => {
                const round = bracket.rounds.find((r) => r.roundNumber === config.roundNumber);
                return (
                  <div key={config.roundNumber} className="bg-card rounded-lg p-4 border border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Stage Name</label>
                        <input
                          type="text"
                          value={config.stageName}
                          onChange={(e) => updateStageConfig(config.roundNumber, 'stageName', e.target.value)}
                          placeholder="e.g., Quarter Finals, Semi Finals, Finals"
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                        />
                      </div>
                      <div className="flex items-end">
                        {round && round.matches.length === 2 && bracket.rounds.length > 2 && (
                          <div className="space-y-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={config.includeThirdPlace}
                                onChange={(e) => updateStageConfig(config.roundNumber, 'includeThirdPlace', e.target.checked)}
                                className="rounded border-border"
                              />
                              <span className="text-sm text-foreground">Include Third Place Match</span>
                            </label>
                            {config.includeThirdPlace && (
                              <input
                                type="text"
                                value={config.thirdPlaceStageName || 'Third Place'}
                                onChange={(e) => updateStageConfig(config.roundNumber, 'thirdPlaceStageName', e.target.value)}
                                placeholder="Third Place / Classification"
                                className="ml-6 w-full rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">Bracket Preview</h3>
              <Button variant="outline" size="sm" onClick={() => setBracket(null)} iconName="Edit" iconPosition="left">
                Edit Teams
              </Button>
            </div>

            <div className="space-y-8 overflow-x-auto">
              {bracket.rounds.map((round) => {
                const stageConfig = stageConfigs.find((c) => c.roundNumber === round.roundNumber);
                const stageName = stageConfig?.stageName || round.name;

                return (
                  <div key={round.roundNumber} className="min-w-max">
                    <div className="flex items-center gap-3 mb-4">
                      <h4 className="text-md font-semibold text-foreground">{stageName}</h4>
                      {round.roundNumber > 1 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-warning/10 text-warning">
                          Requires previous round results
                        </span>
                      )}
                    </div>
                    <div className="space-y-3">
                      {round.matches.map((match) => (
                        <div
                          key={match.id}
                          className={`flex items-center gap-4 p-4 rounded-lg border ${
                            match.isBye ? 'bg-muted/30 border-muted' : 'bg-card border-border'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {match.team1?.logoUrl ? (
                              <Image src={match.team1.logoUrl} alt={match.team1.name} className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                <Icon name="Shield" size={16} className="text-muted-foreground" />
                              </div>
                            )}
                            <span className="font-medium text-foreground">{match.team1?.name || 'TBD'}</span>
                          </div>
                          <span className="text-muted-foreground">vs</span>
                          <div className="flex items-center gap-3 flex-1">
                            {match.team2?.logoUrl ? (
                              <Image src={match.team2.logoUrl} alt={match.team2.name} className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                <Icon name="Shield" size={16} className="text-muted-foreground" />
                              </div>
                            )}
                            <span className="font-medium text-foreground">{match.team2?.name || (match.isBye ? 'Bye' : 'TBD')}</span>
                          </div>
                          {match.isBye && <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">Bye</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                <strong>Start Date:</strong> {bracketSettings.startDate ? new Date(bracketSettings.startDate).toLocaleDateString() : '-'}
              </p>
              {bracketSettings.venue && (
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Venue:</strong> {bracketSettings.venue}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Only Round 1 matches (and any completed byes) have both teams determined, so later rounds will be
                created once earlier results are known. Re-open this tool after each round to schedule the next stage.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setBracket(null)}>
              Back
            </Button>
            <Button variant="default" onClick={saveBracket} loading={isGenerating} iconName="Save" iconPosition="left">
              Save Bracket
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default TournamentKnockoutBracket;
